<template>
  <BContainer
    fluid
    class="mb-4"
  >
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5
        class="fw-bold mb-0"
        style="color: #1e293b;"
      >
        Lost & Found
      </h5>
      <BButton
        variant="primary"
        size="sm"
        @click="openReportModal"
      >
        <i class="fas fa-plus me-1" />
        Report Lost Item
      </BButton>
    </div>

    <!-- Report Form Modal -->
    <BModal
      v-model="showReportForm"
      title="Report a Lost Item"
      size="lg"
      modal-class="student-form-modal"
      centered
      hide-footer
    >
      <BCardText>
        <h6 class="fw-bold mb-3">
          <i class="fas fa-search-location text-warning me-2" />
          Report a Lost Item
        </h6>
        <ValidationObserver ref="formReport">
          <form
            novalidate
            role="form"
            @submit.prevent
          >
            <BRow>
              <BCol
                cols="12"
                md="6"
              >
                <ValidationProvider
                  v-slot="{ errors }"
                  vid="title"
                  name="item title"
                  rules="required|max:255"
                >
                  <BFormGroup
                    label="Item Title"
                    label-for="title"
                    label-class="font-weight-bold"
                    class="mb-2"
                  >
                    <BFormInput
                      id="title"
                      v-model="reportForm.title"
                      placeholder="What did you lose?"
                      maxlength="255"
                      autocomplete="off"
                      :state="errors.length > 0 ? false : null"
                      :disabled="state.reporting"
                    />
                    <div
                      v-if="errors.length > 0"
                      class="invalid-feedback"
                      v-text="errors[0]"
                    />
                  </BFormGroup>
                </ValidationProvider>
              </BCol>

              <BCol
                cols="12"
                md="6"
              >
                <ValidationProvider
                  v-slot="{ errors }"
                  vid="lf_item_category_id"
                  name="category"
                  rules="required"
                >
                  <BFormGroup
                    label="Category"
                    label-for="lf_item_category_id"
                    label-class="font-weight-bold"
                    class="mb-2"
                  >
                    <BFormSelect
                      id="lf_item_category_id"
                      v-model="reportForm.lf_item_category_id"
                      :options="categoryOptions"
                      :state="errors.length > 0 ? false : null"
                      :disabled="state.reporting || state.loadingCategories"
                    >
                      <template #first>
                        <BFormSelectOption
                          :value="null"
                          disabled
                        >
                          {{ state.loadingCategories ? 'Loading...' : 'Select category' }}
                        </BFormSelectOption>
                      </template>
                    </BFormSelect>
                    <div
                      v-if="errors.length > 0"
                      class="invalid-feedback"
                      v-text="errors[0]"
                    />
                  </BFormGroup>
                </ValidationProvider>
              </BCol>

              <BCol
                cols="12"
                md="6"
              >
                <ValidationProvider
                  v-slot="{ errors }"
                  vid="location"
                  name="location"
                  rules="required|max:255"
                >
                  <BFormGroup
                    label="Location Lost"
                    label-for="location"
                    label-class="font-weight-bold"
                    class="mb-2"
                  >
                    <BFormInput
                      id="location"
                      v-model="reportForm.location"
                      placeholder="Where did you lose it?"
                      maxlength="255"
                      autocomplete="off"
                      :state="errors.length > 0 ? false : null"
                      :disabled="state.reporting"
                    />
                    <div
                      v-if="errors.length > 0"
                      class="invalid-feedback"
                      v-text="errors[0]"
                    />
                  </BFormGroup>
                </ValidationProvider>
              </BCol>

              <BCol
                cols="12"
                md="6"
              >
                <ValidationProvider
                  v-slot="{ errors }"
                  vid="date_event"
                  name="date lost"
                  rules="required"
                >
                  <BFormGroup
                    label="Date Lost"
                    label-for="date_event"
                    label-class="font-weight-bold"
                    class="mb-2"
                  >
                    <BFormInput
                      id="date_event"
                      v-model="reportForm.date_event"
                      type="date"
                      :state="errors.length > 0 ? false : null"
                      :disabled="state.reporting"
                    />
                    <div
                      v-if="errors.length > 0"
                      class="invalid-feedback"
                      v-text="errors[0]"
                    />
                  </BFormGroup>
                </ValidationProvider>
              </BCol>

              <BCol cols="12">
                <BFormGroup
                  label="Description (Optional)"
                  label-for="description"
                  label-class="font-weight-bold"
                  class="mb-2"
                >
                  <BFormTextarea
                    id="description"
                    v-model="reportForm.description"
                    placeholder="Describe the item in detail..."
                    rows="3"
                    :disabled="state.reporting"
                  />
                </BFormGroup>
              </BCol>

              <BCol cols="12">
                <BFormGroup
                  label="Item Image (Optional)"
                  label-for="image_file"
                  label-class="font-weight-bold"
                  class="mb-2"
                  description="Supported: JPG, PNG, GIF, WEBP (max 5MB)"
                >
                  <BFormFile
                    id="image_file"
                    :disabled="state.reporting"
                    accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                    placeholder="Choose an image file..."
                    drop-placeholder="Drop image here..."
                    @input="onImageSelected"
                  />
                </BFormGroup>
              </BCol>
            </BRow>

            <div class="d-flex justify-content-end">
              <BButton
                variant="outline-secondary"
                class="me-2"
                :disabled="state.reporting"
                @click="showReportForm = false"
              >
                Cancel
              </BButton>
              <BButton
                variant="primary"
                :disabled="state.reporting"
                @click.prevent="onValidateReport"
              >
                <i :class="['fas me-1', state.reporting ? 'fa-spinner fa-spin' : 'fa-paper-plane']" />
                {{ state.reporting ? 'Submitting...' : 'Submit Report' }}
              </BButton>
            </div>
          </form>
        </ValidationObserver>
      </BCardText>
    </BModal>

    <!-- My Reports List -->
    <BCard class="border shadow-sm">
      <BCardText>
        <BOverlay
          :show="state.loading"
          opacity="0.5"
        >
          <BTable
            :items="reports"
            :fields="tableFields"
            responsive
            stacked="md"
            show-empty
            hover
            class="mb-0"
          >
            <template #empty>
              <div class="text-center py-4">
                <i
                  class="fas fa-box-open mb-2"
                  style="font-size: 2rem; color: #cbd5e1;"
                />
                <p
                  class="small mb-0"
                  style="color: #94a3b8;"
                >
                  No reports found
                </p>
              </div>
            </template>

            <template #cell(reference_number)="{ value }">
              <span class="fw-bold text-primary">{{ value }}</span>
            </template>

            <template #cell(type)="{ value }">
              <BBadge :variant="value === 'found' ? 'success' : 'danger'">
                {{ value === 'found' ? 'Found' : 'Lost' }}
              </BBadge>
            </template>

            <template #cell(category)="{ item }">
              {{ item.category ? item.category.name : '—' }}
            </template>

            <template #cell(status)="{ value }">
              <BBadge :variant="statusVariant(value)">
                {{ value }}
              </BBadge>
            </template>

            <template #cell(image)="{ item }">
              <BButton
                v-if="item.image_url"
                size="sm"
                variant="outline-primary"
                @click.prevent="openImagePreview(item.image_url)"
              >
                View
              </BButton>
              <span v-else>—</span>
            </template>
          </BTable>
        </BOverlay>

        <div
          v-if="pagination.last_page > 1"
          stacked="md"
          class="d-flex justify-content-end mt-3"
        >
          <BPagination
            v-model="pagination.current_page"
            :total-rows="pagination.total"
            :per-page="pagination.per_page"
            size="sm"
            @change="onPageChange"
          />
        </div>
      </BCardText>
    </BCard>

    <BModal
      ref="modalImagePreview"
      title="Item Image"
      size="lg"
      centered
      hide-footer
    >
      <div
        v-if="previewImageUrl"
        class="text-center"
      >
        <img
          :src="previewImageUrl"
          alt="Lost item"
          class="img-fluid rounded border"
        >
      </div>
      <div
        v-else
        class="text-center text-muted py-3"
      >
        No image available.
      </div>
    </BModal>
  </BContainer>
</template>

<script>
import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import { SStudentLostItem } from '@/services/student'
import { SSharedList } from '@/services/shared'

export default {
  name: 'StudentLostAndFound',

  mixins: [MxMisc, MxFormatter],

  middleware: ['auth', 'student'],

  metaInfo: () => ({ title: 'Lost & Found' }),

  data () {
    return {
      state: { loading: false, reporting: false, loadingCategories: false },
      showReportForm: false,
      reportForm: {
        title: '',
        lf_item_category_id: null,
        location: '',
        date_event: '',
        description: '',
        image_file: null
      },
      reports: [],
      categories: [],
      pagination: { current_page: 1, per_page: 10, total: 0, last_page: 1 },
      previewImageUrl: ''
    }
  },

  computed: {
    tableFields () {
      return [
        { key: 'reference_number', label: 'Ref #', sortable: true },
        { key: 'type', label: 'Type' },
        { key: 'title', label: 'Item' },
        { key: 'category', label: 'Category' },
        { key: 'location', label: 'Location' },
        { key: 'image', label: 'Image' },
        { key: 'date_event', label: 'Date' },
        { key: 'status', label: 'Status', sortable: true }
      ]
    },

    categoryOptions () {
      return this.categories.map(c => ({ value: c.id, text: c.name }))
    }
  },

  mounted () {
    this.fetchReports()
  },

  methods: {
    openReportModal () {
      this.showReportForm = true
      this.fetchCategories()
    },

    statusVariant (status) {
      const v = { pending: 'warning', verified: 'info', claimed: 'success', returned: 'success', unclaimed: 'secondary' }
      return v[status] || 'secondary'
    },

    getStorageFilename (path) {
      if (!path || typeof path !== 'string') return ''
      const parts = path.split('/')
      return parts.length ? parts[parts.length - 1] : ''
    },

    getLfImageUrl (path) {
      const filename = this.getStorageFilename(path)
      if (!filename) return ''
      const apiBase = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '')
      return `${apiBase}/shared/uploads/lf-items/images/${encodeURIComponent(filename)}`
    },

    openImagePreview (path) {
      this.previewImageUrl = this.getLfImageUrl(path)
      this.$refs.modalImagePreview.show()
    },

    onPageChange (page) {
      this.pagination.current_page = page
      this.fetchReports()
    },

    async fetchCategories () {
      this.state.loadingCategories = true
      try {
        const { data } = await SSharedList.getActiveLfItemCategories({ per_page: 500, sort: 'name', sort_desc: false })
        this.categories = data.data || []
      } catch {
        this.categories = []
      } finally {
        this.state.loadingCategories = false
      }
    },

    async fetchReports () {
      this.state.loading = true
      try {
        const { data } = await SStudentLostItem.myReports({
          page: this.pagination.current_page,
          per_page: this.pagination.per_page
        })
        this.reports = data.data || data.items || []
        if (data.pagination) {
          this.pagination = { ...this.pagination, ...data.pagination }
        } else if (data.total_rows != null) {
          this.pagination.total = data.total_rows
          this.pagination.last_page = Math.ceil(data.total_rows / this.pagination.per_page)
        }
      } catch {
        this.reports = []
      } finally {
        this.state.loading = false
      }
    },

    async onValidateReport () {
      await this.$refs.formReport.validate().then(async valid => {
        if (!valid) return this.swalInvalid()
        this.swalConfirm({
          html: '<h6>Submit this lost item report?</h6>',
          confirmButtonText: 'Submit',
          preConfirm: () => {
            this.state.reporting = true
            return this.onPostReport()
          }
        })
      })
    },

    async onPostReport () {
      return SStudentLostItem.report(this.reportForm)
        .then(({ data }) => {
          return this.swalSuccess(data.message || 'Report submitted successfully.').then(() => {
            this.showReportForm = false
            this.reportForm = {
              title: '',
              lf_item_category_id: null,
              location: '',
              date_event: '',
              description: '',
              image_file: null
            }
            this.$refs.formReport.reset()
            this.fetchReports()
          })
        })
        .catch(({ message }) => {
          if (this.$refs.formReport && message?.errors) {
            this.$refs.formReport.setErrors(message.errors)
          }
          return message
        })
        .finally(() => {
          this.state.reporting = false
        })
    },

    onImageSelected (file) {
      this.reportForm.image_file = file || null
    }
  }
}
</script>

<style scoped>
:deep(.student-form-modal .modal-header) {
  border-bottom: 1px solid #e2e8f0;
}

:deep(.student-form-modal .modal-footer) {
  border-top: 1px solid #e2e8f0;
}

@media (max-width: 575.98px) {
  :deep(.student-form-modal .modal-dialog) {
    margin: 0;
    max-width: 100%;
    min-height: 100vh;
  }

  :deep(.student-form-modal .modal-content) {
    min-height: 100vh;
    border-radius: 0;
  }
}
</style>
